
import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, SendIcon, User, Mail, MessageSquare, FileText } from "lucide-react";
import { ContactFormData } from "@/utils/formValidation";

interface ContactFormFieldsProps {
  formData: ContactFormData;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  isSubmitting: boolean;
}

const ContactFormFields: React.FC<ContactFormFieldsProps> = ({
  formData,
  handleChange,
  handleSubmit,
  isSubmitting,
}) => {
  return (
    <form className="space-y-6 bg-white p-6 sm:p-8 rounded-xl border border-joerg-100 shadow-sm" onSubmit={handleSubmit}>
      {/* Honeypot field - hidden from humans, bots will fill it */}
      <div className="absolute opacity-0 -z-10 h-0 w-0 overflow-hidden" aria-hidden="true">
        <label htmlFor="website_url">Website</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          tabIndex={-1}
          autoComplete="off"
          onChange={handleChange}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-joerg-700 mb-2 flex items-center">
            <User className="h-4 w-4 mr-2 text-joerg-600" />
            Name <span className="text-red-500 ml-1">*</span>
          </label>
          <Input
            id="name"
            type="text"
            placeholder="Ihr Name"
            className="w-full border-joerg-200 focus:border-primary"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-joerg-700 mb-2 flex items-center">
            <Mail className="h-4 w-4 mr-2 text-joerg-600" />
            E-Mail <span className="text-red-500 ml-1">*</span>
          </label>
          <Input
            id="email"
            type="email"
            placeholder="Ihre E-Mail"
            className="w-full border-joerg-200 focus:border-primary"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
      </div>
      <div>
        <label htmlFor="subject" className="block text-sm font-medium text-joerg-700 mb-2 flex items-center">
          <MessageSquare className="h-4 w-4 mr-2 text-joerg-600" />
          Betreff
        </label>
        <Input
          id="subject"
          type="text"
          placeholder="Betreff Ihrer Nachricht"
          className="w-full border-joerg-200 focus:border-primary"
          value={formData.subject}
          onChange={handleChange}
        />
      </div>
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-joerg-700 mb-2 flex items-center">
          <MessageSquare className="h-4 w-4 mr-2 text-joerg-600" />
          Nachricht <span className="text-red-500 ml-1">*</span>
        </label>
        <Textarea
          id="message"
          placeholder="Ihre Nachricht"
          rows={5}
          className="w-full border-joerg-200 focus:border-primary"
          value={formData.message}
          onChange={handleChange}
          required
        />
      </div>
      
      <div className="flex items-center text-sm text-joerg-600 bg-joerg-50 p-3 rounded-lg">
        <ShieldCheck className="h-4 w-4 mr-2 text-green-500" />
        <span>Spam-Schutz aktiv – keine externen Dienste, 100 % DSGVO-konform</span>
      </div>
      
      {/* DSGVO Consent Checkbox */}
      <div className="space-y-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
        <div className="flex items-start space-x-3">
          <Checkbox 
            id="consent_given"
            checked={formData.consent_given}
            onCheckedChange={(checked) => {
              handleChange({
                target: { 
                  id: 'consent_given', 
                  type: 'checkbox', 
                  checked: checked === true 
                }
              } as React.ChangeEvent<HTMLInputElement>);
            }}
            className="mt-0.5"
          />
          <div className="flex-1">
            <label htmlFor="consent_given" className="text-sm font-medium text-joerg-700 cursor-pointer flex items-start">
              <FileText className="h-4 w-4 mr-2 mt-0.5 text-joerg-600 flex-shrink-0" />
              <span>
                Datenschutzerklärung <span className="text-red-500">*</span>
              </span>
            </label>
            <p className="text-xs text-joerg-600 mt-1 ml-6">
              Mit dem Absenden des Formulars erkläre ich mich mit der Verarbeitung meiner Daten gemäß der{" "}
              <a 
                href="/datenschutz" 
                className="text-primary hover:underline font-medium"
                target="_blank"
                rel="noopener noreferrer"
              >
                Datenschutzerklärung
              </a>{" "}
              einverstanden. Zur Vermeidung von Spam nutzen wir technische Schutzmaßnahmen im Rahmen unseres berechtigten Interesses.
              Die Daten werden DSGVO-konform verarbeitet und nach 30 Tagen automatisch gelöscht.
            </p>
          </div>
        </div>
      </div>
      
      <div>
        <Button 
          type="submit" 
          className="w-full md:w-auto flex items-center gap-2 bg-joerg-700 hover:bg-joerg-800 text-white py-2.5"
          disabled={isSubmitting || !formData.consent_given}
        >
          {isSubmitting ? (
            <>Wird gesendet...</>
          ) : (    
            <>
              <SendIcon className="h-4 w-4" />
              Nachricht senden
            </>
          )}
        </Button>
        <p className="mt-3 text-xs text-joerg-500">
          {!formData.consent_given && (
            <span className="text-red-500 block mb-1">
              Bitte akzeptieren Sie die Datenschutzerklärung, um die Nachricht zu senden.
            </span>
          )}
        </p>
      </div>
    </form>
  );
};

export default ContactFormFields;
